import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { 
  Key,
  Shield,
  Building2,
  ExternalLink,
  Copy,
  Check,
  AlertCircle,
  Loader2,
  Info
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SSOConfiguration = () => {
  const { user } = useAuth();
  const [providers, setProviders] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [selectedProvider, setSelectedProvider] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  
  const [formData, setFormData] = useState({
    saml_entity_id: '',
    saml_sso_url: '',
    saml_certificate: '',
    oidc_client_id: '',
    oidc_client_secret: '',
    oidc_issuer_url: '',
    auto_provision_users: true,
    default_role: 'employee',
    allowed_domains: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [providersRes, configsRes] = await Promise.all([
        axios.get(`${API}/sso/providers`, { withCredentials: true }),
        axios.get(`${API}/sso/config`, { withCredentials: true })
      ]);
      setProviders(providersRes.data.providers);
      setConfigs(configsRes.data);
      
      if (user?.role === 'super_admin') {
        const companiesRes = await axios.get(`${API}/companies`, { withCredentials: true });
        setCompanies(companiesRes.data);
      }
    } catch (error) {
      console.error("Error fetching SSO data:", error);
      toast.error("Failed to load SSO configuration");
    } finally {
      setLoading(false);
    }
  };

  const handleProviderChange = (providerId) => {
    setSelectedProvider(providerId);
    // Reset form when provider changes
    setFormData({
      saml_entity_id: '',
      saml_sso_url: '',
      saml_certificate: '',
      oidc_client_id: '',
      oidc_client_secret: '',
      oidc_issuer_url: '',
      auto_provision_users: true,
      default_role: 'employee',
      allowed_domains: ''
    });
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveConfig = async () => {
    if (!selectedCompany) {
      toast.error("Please select a company");
      return;
    }
    if (!selectedProvider) {
      toast.error("Please select an SSO provider");
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/sso/config/${selectedCompany}`, {
        provider: selectedProvider,
        ...formData,
        allowed_domains: formData.allowed_domains.split(',').map(d => d.trim()).filter(d => d)
      }, { withCredentials: true });
      
      toast.success("SSO configuration saved!");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save configuration");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleSSO = async (companyId, enabled) => {
    try {
      await axios.put(`${API}/sso/config/${companyId}/enable?enabled=${enabled}`, {}, { withCredentials: true });
      toast.success(`SSO ${enabled ? 'enabled' : 'disabled'}`);
      fetchData();
    } catch (error) {
      toast.error("Failed to toggle SSO");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success("Copied to clipboard");
  };

  const getCallbackUrl = (provider) => {
    const baseUrl = window.location.origin;
    if (provider === 'saml') {
      return `${BACKEND_URL}/api/auth/sso/saml/callback`;
    }
    return `${BACKEND_URL}/api/auth/sso/oidc/callback`;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="w-12 h-12 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  const selectedProviderData = providers.find(p => p.id === selectedProvider);

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 space-y-6" data-testid="sso-configuration-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
            <Key className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="font-heading text-2xl lg:text-3xl font-bold text-primary">
              SSO Configuration
            </h1>
            <p className="text-muted-foreground mt-1">
              Configure Single Sign-On for enterprise authentication.
            </p>
          </div>
        </div>

        <Tabs defaultValue="configure">
          <TabsList>
            <TabsTrigger value="configure">Configure SSO</TabsTrigger>
            <TabsTrigger value="existing">Existing Configurations</TabsTrigger>
            <TabsTrigger value="docs">Setup Guide</TabsTrigger>
          </TabsList>

          {/* Configure Tab */}
          <TabsContent value="configure" className="space-y-6">
            {/* Company Selection */}
            {user?.role === 'super_admin' && (
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <Building2 className="w-5 h-5" />
                    Select Company
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Select value={selectedCompany} onValueChange={setSelectedCompany}>
                    <SelectTrigger data-testid="company-select">
                      <SelectValue placeholder="Select a company..." />
                    </SelectTrigger>
                    <SelectContent>
                      {companies.map((company) => (
                        <SelectItem key={company.company_id} value={company.company_id}>
                          {company.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </CardContent>
              </Card>
            )}

            {/* Provider Selection */}
            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-lg">Select SSO Provider</CardTitle>
                <CardDescription>Choose your identity provider</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {providers.map((provider) => (
                    <div
                      key={provider.id}
                      onClick={() => handleProviderChange(provider.id)}
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        selectedProvider === provider.id
                          ? 'border-accent bg-accent/5'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                      data-testid={`provider-${provider.id}`}
                    >
                      <h4 className="font-semibold text-primary">{provider.name}</h4>
                      <p className="text-sm text-muted-foreground mt-1">{provider.description}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Configuration Form */}
            {selectedProvider && (
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading text-lg">
                    {selectedProviderData?.name} Configuration
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Callback URL */}
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-blue-900">Callback URL (Add this to your IdP)</p>
                        <div className="flex items-center gap-2 mt-2">
                          <code className="text-xs bg-white px-2 py-1 rounded border flex-1 overflow-x-auto">
                            {getCallbackUrl(selectedProvider)}
                          </code>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => copyToClipboard(getCallbackUrl(selectedProvider))}
                          >
                            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* SAML Fields */}
                  {selectedProvider === 'saml' && (
                    <>
                      <div>
                        <Label htmlFor="saml_entity_id">Entity ID (Issuer)</Label>
                        <Input
                          id="saml_entity_id"
                          value={formData.saml_entity_id}
                          onChange={(e) => handleInputChange('saml_entity_id', e.target.value)}
                          placeholder="https://your-idp.com/entity-id"
                        />
                      </div>
                      <div>
                        <Label htmlFor="saml_sso_url">SSO URL</Label>
                        <Input
                          id="saml_sso_url"
                          value={formData.saml_sso_url}
                          onChange={(e) => handleInputChange('saml_sso_url', e.target.value)}
                          placeholder="https://your-idp.com/sso/saml"
                        />
                      </div>
                      <div>
                        <Label htmlFor="saml_certificate">X.509 Certificate</Label>
                        <Textarea
                          id="saml_certificate"
                          value={formData.saml_certificate}
                          onChange={(e) => handleInputChange('saml_certificate', e.target.value)}
                          placeholder="-----BEGIN CERTIFICATE-----&#10;...&#10;-----END CERTIFICATE-----"
                          className="font-mono text-xs min-h-[150px]"
                        />
                      </div>
                    </>
                  )}

                  {/* OIDC Fields */}
                  {['oidc', 'azure_ad', 'okta', 'google_workspace'].includes(selectedProvider) && (
                    <>
                      <div>
                        <Label htmlFor="oidc_client_id">Client ID</Label>
                        <Input
                          id="oidc_client_id"
                          value={formData.oidc_client_id}
                          onChange={(e) => handleInputChange('oidc_client_id', e.target.value)}
                          placeholder="your-client-id"
                        />
                      </div>
                      <div>
                        <Label htmlFor="oidc_client_secret">Client Secret</Label>
                        <Input
                          id="oidc_client_secret"
                          type="password"
                          value={formData.oidc_client_secret}
                          onChange={(e) => handleInputChange('oidc_client_secret', e.target.value)}
                          placeholder="your-client-secret"
                        />
                      </div>
                      <div>
                        <Label htmlFor="oidc_issuer_url">Issuer URL</Label>
                        <Input
                          id="oidc_issuer_url"
                          value={formData.oidc_issuer_url}
                          onChange={(e) => handleInputChange('oidc_issuer_url', e.target.value)}
                          placeholder={
                            selectedProvider === 'azure_ad' ? 'https://login.microsoftonline.com/{tenant}/v2.0' :
                            selectedProvider === 'okta' ? 'https://your-domain.okta.com/oauth2/default' :
                            selectedProvider === 'google_workspace' ? 'https://accounts.google.com' :
                            'https://your-issuer-url'
                          }
                        />
                      </div>
                    </>
                  )}

                  {/* Common Settings */}
                  <div className="pt-4 border-t">
                    <h4 className="font-medium mb-4">User Provisioning Settings</h4>
                    
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <Label>Auto-provision users</Label>
                        <p className="text-sm text-muted-foreground">
                          Automatically create accounts for new SSO users
                        </p>
                      </div>
                      <Switch
                        checked={formData.auto_provision_users}
                        onCheckedChange={(v) => handleInputChange('auto_provision_users', v)}
                      />
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="default_role">Default Role</Label>
                        <Select value={formData.default_role} onValueChange={(v) => handleInputChange('default_role', v)}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="employee">Employee</SelectItem>
                            <SelectItem value="company_admin">Company Admin</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="allowed_domains">Allowed Email Domains</Label>
                        <Input
                          id="allowed_domains"
                          value={formData.allowed_domains}
                          onChange={(e) => handleInputChange('allowed_domains', e.target.value)}
                          placeholder="company.com, subsidiary.com"
                        />
                        <p className="text-xs text-muted-foreground mt-1">Comma-separated list</p>
                      </div>
                    </div>
                  </div>

                  <Button onClick={handleSaveConfig} disabled={saving} className="w-full">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Shield className="w-4 h-4 mr-2" />}
                    Save Configuration
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Existing Configurations Tab */}
          <TabsContent value="existing" className="space-y-4">
            {configs.length > 0 ? (
              configs.map((config) => (
                <Card key={config.config_id}>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                          <Key className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <h4 className="font-semibold">{config.provider?.toUpperCase()}</h4>
                          <p className="text-sm text-muted-foreground">Company: {config.company_id}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <Badge className={config.enabled ? 'badge-success' : 'badge-neutral'}>
                          {config.enabled ? 'Enabled' : 'Disabled'}
                        </Badge>
                        <Switch
                          checked={config.enabled}
                          onCheckedChange={(v) => handleToggleSSO(config.company_id, v)}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Card>
                <CardContent className="p-12 text-center">
                  <Key className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
                  <p className="text-muted-foreground">No SSO configurations found</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Documentation Tab */}
          <TabsContent value="docs" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-lg">SSO Setup Guide</CardTitle>
                <CardDescription>
                  Step-by-step instructions for each identity provider
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Accordion type="single" collapsible>
                  {providers.map((provider) => (
                    <AccordionItem key={provider.id} value={provider.id}>
                      <AccordionTrigger>
                        <span className="font-medium">{provider.name}</span>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-4 pt-4">
                          <p className="text-sm text-muted-foreground">{provider.description}</p>
                          
                          <div>
                            <h5 className="font-medium mb-2">Required Fields:</h5>
                            <ul className="list-disc list-inside text-sm space-y-1">
                              {provider.required_fields.map((field) => (
                                <li key={field}><code className="bg-slate-100 px-1 rounded">{field}</code></li>
                              ))}
                            </ul>
                          </div>
                          
                          <div>
                            <h5 className="font-medium mb-2">Setup Instructions:</h5>
                            <pre className="text-sm bg-slate-50 p-4 rounded-lg whitespace-pre-wrap overflow-x-auto">
                              {provider.setup_instructions.replace(/{base_url}/g, BACKEND_URL)}
                            </pre>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default SSOConfiguration;
